import React from 'react';

import { Row } from '~components/table-view';

import { ServiceTypeRow } from '~appointments/reducers/serviceTypes';

interface ComponentProps {
  row: Row;
  serviceTypes: ServiceTypeRow[];
}

/**
 * The local service types associated with a service, one badge per line.
 *
 * Comma-delimited names ran together into an unreadable block once a service carried more than two
 * or three - service type names here are long and several share a prefix, so the eye had nothing to
 * anchor on. One per line makes the count obvious at a glance, which is the thing a provider is
 * actually checking before a cutover.
 *
 * Read-only by design: no remove control. Removing an association is a save, and offering an X on a
 * row that is not in edit mode invited a click that either did nothing or silently changed the
 * setup. The removable chips live in the editor, where a save follows.
 */
// Called directly as `AssociatedServices({ row, serviceTypes })` from list.ts's `renderReadOnly`
// (a .ts file, so no JSX is available at the call site), not rendered as `<AssociatedServices />` -
// typed as a plain function rather than `React.FC` so it reads as the render helper it is, not a
// component that could safely pick up a hook.
const AssociatedServices = ({ row, serviceTypes }: ComponentProps) => {
  const ids = (row.service_type_ids ?? []) as number[];

  if (ids.length === 0) return <span className="associated-services empty">-</span>;

  const nameById = new Map(serviceTypes.map(({ id, name }) => [id, name]));

  return (
    <div className="associated-services">
      {ids.map((id) => (
        // A service type deleted after it was associated still has a row here, so it is named by
        // id rather than dropped - a silently shorter list would read as "no longer associated".
        <span key={id} className="badge">
          {nameById.get(id) ?? `Unknown service type (${id})`}
        </span>
      ))}
    </div>
  );
};

export default AssociatedServices;
